import {
  manifestUtils,
  type TestRecordIssue
} from '@deque/axe-devtools-issues-utils';
import React from 'react';

import {
  getTest,
  getTestIssues,
  getTestManifests,
  type Test,
  type Manifests
} from '../../common/api-client';
import useAPI from '../../common/hooks/useAPI';
import { useAuthContext } from '../../common/contexts/auth';
import { type ManifestRerunGroupsByType } from '@deque/axe-devtools-issues-utils/dist/utils/manifest-utils';

export interface State {
  loading: boolean;
  error: Error | null;
  test: Test | null;
  issues: TestRecordIssue[];
  manifests: Manifests | null;
  manifestRerunGroupsByType: ManifestRerunGroupsByType | null;
  refetch: () => void;
}

const context = React.createContext<State>({
  loading: false,
  error: null,
  test: null,
  issues: [],
  manifests: null,
  manifestRerunGroupsByType: null,
  refetch: () => {
    throw new Error('Not implemented');
  }
});

interface Props {
  testId: string;
  children: React.ReactNode;
  /**
   * A custom data loader for test purposes only
   */
  customDataLoader?: () => Promise<
    Omit<State, 'loading' | 'error' | 'refetch'>
  >;
}

export const TestProvider: React.FC<Props> = ({
  customDataLoader,
  testId,
  children
}) => {
  const { user } = useAuthContext();
  const dataLoader = React.useCallback(async () => {
    const [test, issues, manifests] = await Promise.all([
      getTest(testId, user?.token),
      getTestIssues(testId, user?.token),
      getTestManifests(testId, user?.token)
    ]);

    const manifestRerunGroupsByType =
      manifestUtils.getManifestRerunGroupsByType(manifests);
    const currentManifestIds = manifestUtils.getCurrentManifestIds(
      manifestUtils.getManifestRerunGroups(Object.values(manifests).flat())
    );
    const currentIssues = issues.filter(
      issue => !issue.manifest_id || currentManifestIds.has(issue.manifest_id)
    );

    return {
      test,
      issues: currentIssues,
      manifests,
      manifestRerunGroupsByType
    };
  }, [user?.token, testId]);
  const state = useAPI(customDataLoader || dataLoader);

  return (
    <context.Provider
      value={{
        loading: state.loading,
        error: state.error,
        test: state.data?.test ?? null,
        issues: state.data?.issues ?? [],
        manifests: state.data?.manifests ?? null,
        manifestRerunGroupsByType:
          state.data?.manifestRerunGroupsByType ?? null,
        refetch: state.refresh
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useTest = (): State => React.useContext(context);
